import { lazy, Suspense, type ReactNode } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { AppShell } from './layout/AppShell'
import { EmDesenvolvimento, CarregandoTela } from './components/EmDesenvolvimento'
import { Protegido } from './components/Protegido'

/**
 * Roteador ÚNICO. Uma tela por rota; trocar de rota desmonta a anterior.
 *
 * As rotas são exatamente as do sistema atual — não podem mudar, senão quebram
 * links salvos e o hábito da equipe.
 *
 * Telas pesadas entram por `lazy()`: cada uma vira um chunk próprio, então abrir
 * o Dashboard não baixa o código da DUIMP inteira.
 *
 * **Toda rota passa por `Protegido`**, que verifica a permissão da tela. Assim
 * digitar `/duimp` na barra de endereço sem ter acesso mostra uma explicação com
 * caminho de volta, em vez de carregar a tela e falhar.
 */

const DuimpPage = lazy(() =>
  import('./features/duimp/DuimpPage').then((m) => ({ default: m.DuimpPage })),
)
const ProcessosList = lazy(() =>
  import('./features/processos/ProcessosList').then((m) => ({ default: m.ProcessosList })),
)
const ProcessoForm = lazy(() =>
  import('./features/processos/ProcessoForm').then((m) => ({ default: m.ProcessoForm })),
)
const EmpresasList = lazy(() =>
  import('./features/empresas/EmpresasList').then((m) => ({ default: m.EmpresasList })),
)
const EmpresaForm = lazy(() =>
  import('./features/empresas/EmpresaForm').then((m) => ({ default: m.EmpresaForm })),
)
const Dashboard = lazy(() =>
  import('./features/dashboard/Dashboard').then((m) => ({ default: m.Dashboard })),
)
const AwbList = lazy(() => import('./features/awb/AwbList').then((m) => ({ default: m.AwbList })))
const AwbForm = lazy(() => import('./features/awb/AwbForm').then((m) => ({ default: m.AwbForm })))
const EstimativasList = lazy(() =>
  import('./features/estimativas/EstimativasList').then((m) => ({ default: m.EstimativasList })),
)
const EstimativaForm = lazy(() =>
  import('./features/estimativas/EstimativaForm').then((m) => ({ default: m.EstimativaForm })),
)
const Login = lazy(() => import('./features/auth/Login').then((m) => ({ default: m.Login })))
const Configuracoes = lazy(() =>
  import('./features/config/Configuracoes').then((m) => ({ default: m.Configuracoes })),
)
const Admin = lazy(() => import('./features/admin/Admin').then((m) => ({ default: m.Admin })))
const RetificacaoPage = lazy(() =>
  import('./features/retificacao/RetificacaoPage').then((m) => ({ default: m.RetificacaoPage })),
)
const MapeamentoPage = lazy(() =>
  import('./features/mapeamento/MapeamentoPage').then((m) => ({ default: m.MapeamentoPage })),
)
const HistoricoPage = lazy(() =>
  import('./features/historico/HistoricoPage').then((m) => ({ default: m.HistoricoPage })),
)
const LotePage = lazy(() => import('./features/lote/LotePage').then((m) => ({ default: m.LotePage })))
const NovaLpcoPage = lazy(() =>
  import('./features/lpco/NovaLpcoPage').then((m) => ({ default: m.NovaLpcoPage })),
)
const PainelLpco = lazy(() =>
  import('./features/lpco/PainelLpco').then((m) => ({ default: m.PainelLpco })),
)
const AnexarLpcoPage = lazy(() =>
  import('./features/lpco/AnexarLpcoPage').then((m) => ({ default: m.AnexarLpcoPage })),
)
const PdfFerramentas = lazy(() => import('./features/pdf/PdfFerramentas').then((m) => ({ default: m.PdfFerramentas })))
const FinanceiroPage = lazy(() =>
  import('./features/financeiro/FinanceiroPage').then((m) => ({ default: m.FinanceiroPage })),
)
const FichaEmpresa = lazy(() =>
  import('./features/empresas/FichaEmpresa').then((m) => ({ default: m.FichaEmpresa })),
)
const AlertasVencimento = lazy(() =>
  import('./features/processos/AlertasVencimento').then((m) => ({ default: m.AlertasVencimento })),
)
const CatalogoPage = lazy(() =>
  import('./features/catalogo/CatalogoPage').then((m) => ({ default: m.CatalogoPage })),
)

/**
 * Monta a rota com verificação de permissão e carregamento sob demanda.
 * `permissao` é a chave da tela — por padrão, a própria rota.
 */
function tela(node: ReactNode, permissao: string) {
  return (
    <Protegido rota={permissao}>
      <Suspense fallback={<CarregandoTela />}>{node}</Suspense>
    </Protegido>
  )
}

/*
  O `wip()` — placeholder de tela não construída — foi removido: **todas as telas
  do escopo existem**. `EmDesenvolvimento` continua em uso na rota-coringa (404).
*/

export const router = createBrowserRouter([
  // Login fica FORA do AppShell — é a única tela sem sidebar.
  {
    path: '/login',
    element: (
      <Suspense fallback={<CarregandoTela />}>
        <Login />
      </Suspense>
    ),
  },
  {
    path: '/',
    element: <AppShell />,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },

      { path: 'dashboard', element: tela(<Dashboard />, '/dashboard') },

      // ---------------------------------------------------------- Importação
      { path: 'importacao', element: tela(<ProcessosList tipo="importacao" />, '/importacao') },
      { path: 'importacao/novo', element: tela(<ProcessoForm tipo="importacao" />, '/importacao') },
      { path: 'importacao/:id', element: tela(<ProcessoForm tipo="importacao" />, '/importacao') },

      { path: 'estimativas', element: tela(<EstimativasList />, '/estimativas') },
      { path: 'estimativas/nova', element: tela(<EstimativaForm />, '/estimativas') },
      { path: 'estimativas/:id', element: tela(<EstimativaForm />, '/estimativas') },

      { path: 'catalog', element: tela(<CatalogoPage />, '/catalog') },

      // ---------------------------------------------------------------- LPCO
      { path: 'lpco/new', element: tela(<NovaLpcoPage />, '/lpco/new') },
      { path: 'lpco/anexar', element: tela(<AnexarLpcoPage />, '/lpco/anexar') },
      { path: 'lpco/painel', element: tela(<PainelLpco />, '/lpco/painel') },
      { path: 'batch', element: tela(<LotePage />, '/batch') },
      { path: 'history', element: tela(<HistoricoPage />, '/history') },
      { path: 'mappings', element: tela(<MapeamentoPage />, '/mappings') },

      // --------------------------------------------------------------- DUIMP
      // Uma rota por aba: o item "Retificar DUIMP" do menu abre direto na aba
      // certa, e o endereço fica compartilhável.
      { path: 'duimp', element: tela(<DuimpPage />, '/duimp') },
      { path: 'duimp/rascunhos', element: tela(<DuimpPage />, '/duimp') },
      { path: 'duimp/historico', element: tela(<DuimpPage />, '/duimp') },
      { path: 'duimp/ia', element: tela(<DuimpPage />, '/duimp') },
      { path: 'duimp/custos', element: tela(<DuimpPage />, '/duimp') },
      { path: 'duimp/minhas', element: tela(<DuimpPage />, '/duimp') },
      { path: 'duimp/retificacao', element: tela(<RetificacaoPage />, '/duimp/retificacao') },

      // ----------------------------------------------------------- Exportação
      { path: 'exportacao', element: tela(<ProcessosList tipo="exportacao" />, '/exportacao') },
      { path: 'exportacao/novo', element: tela(<ProcessoForm tipo="exportacao" />, '/exportacao') },
      { path: 'exportacao/:id', element: tela(<ProcessoForm tipo="exportacao" />, '/exportacao') },

      // --------------------------------------------------------- Agenciamento
      { path: 'agenciamento', element: tela(<ProcessosList tipo="agenciamento" />, '/agenciamento') },
      { path: 'agenciamento/novo', element: tela(<ProcessoForm tipo="agenciamento" />, '/agenciamento') },
      { path: 'agenciamento/:id', element: tela(<ProcessoForm tipo="agenciamento" />, '/agenciamento') },

      // ------------------------------------------------------- Credenciamento
      { path: 'credenciamento', element: tela(<ProcessosList tipo="credenciamento" />, '/credenciamento') },
      { path: 'credenciamento/novo', element: tela(<ProcessoForm tipo="credenciamento" />, '/credenciamento') },
      // Painel consolidado: todos os vencimentos, de todos os processos. Diferente
      // da aba do processo, que mostra os de um só.
      {
        path: 'credenciamento/alertas',
        element: tela(<AlertasVencimento />, '/credenciamento/alertas'),
      },
      { path: 'credenciamento/:id', element: tela(<ProcessoForm tipo="credenciamento" />, '/credenciamento') },

      // ------------------------------------------------------------- Empresas
      { path: 'empresas', element: tela(<EmpresasList />, '/empresas') },
      { path: 'empresas/nova', element: tela(<EmpresaForm />, '/empresas') },
      // A ficha é a tela de leitura; a edição fica em /editar. Antes clicar na
      // empresa caía direto no formulário, sem visão do que ela movimenta.
      { path: 'empresas/:id', element: tela(<FichaEmpresa />, '/empresas') },
      { path: 'empresas/:id/editar', element: tela(<EmpresaForm />, '/empresas') },

      // ------------------------------------------------------------- Restantes
      { path: 'ferramentas/pdf', element: tela(<PdfFerramentas />, '/ferramentas/pdf') },
      { path: 'financeiro', element: tela(<FinanceiroPage />, '/financeiro') },
      { path: 'awb', element: tela(<AwbList />, '/awb') },
      { path: 'awb/novo', element: tela(<AwbForm />, '/awb') },
      { path: 'awb/:id', element: tela(<AwbForm />, '/awb') },
      { path: 'settings', element: tela(<Configuracoes />, '/settings') },
      { path: 'admin', element: tela(<Admin />, '/admin') },

      { path: '*', element: <EmDesenvolvimento nome="Página não encontrada" /> },
    ],
  },
])
